import {
  TechnicalSupportRadioValues,
  TechnicalSupportStatus,
} from "../../types/globalTypes";
import { useTranslation } from "react-i18next";

type SupportStatusCardsProps = {
  status: TechnicalSupportStatus;
  type: TechnicalSupportRadioValues;
  subject: string;
  message: string;
  imgSrc?: string;
};

function SupportStatusCards({
  status,
  message,
  subject,
  type,
  imgSrc,
}: SupportStatusCardsProps) {
  const { t } = useTranslation();

  const statusStyles: Record<
    TechnicalSupportStatus,
    { bgColor: string; textColor: string }
  > = {
    pending: {
      bgColor: "bg-yellow-300",
      textColor: "text-yellow-600",
    },
    open: {
      bgColor: "bg-green-300",
      textColor: "text-green-600",
    },
    reviewing: {
      bgColor: "bg-blue-300",
      textColor: "text-blue-600",
    },
    approved: {
      bgColor: "bg-teal-300",
      textColor: "text-teal-600",
    },
    rejected: {
      bgColor: "bg-red-300",
      textColor: "text-red-600",
    },
  };

  const typeStyles: Record<
    TechnicalSupportRadioValues,
    { bgColor: string; textColor: string }
  > = {
    feedback: {
      bgColor: "bg-gray-300",
      textColor: "text-gray-600",
    },
    suggestion: {
      bgColor: "bg-orange-300",
      textColor: "text-orange-600",
    },
    bug: {
      bgColor: "bg-red-400",
      textColor: "text-red-700",
    },
    report: {
      bgColor: "bg-blue-400",
      textColor: "text-blue-700",
    },
    feature: {
      bgColor: "bg-teal-400",
      textColor: "text-teal-700",
    },
  };

  const getTextDirection = (text: string): "ltr" | "rtl" => {
    const englishRegex = /^[A-Za-z0-9\s!@#$%^&*(),.?":{}|<>[\]\\';`~\-_=+]+$/;
    return englishRegex.test(text) ? "ltr" : "rtl";
  };

  const subjectDirection = getTextDirection(subject);
  const bodyDirection = getTextDirection(message);

  return (
    <div className="w-full max-w-sm rounded-lg bg-white shadow-md">
      <div className="relative aspect-square h-[250px] w-full overflow-hidden rounded-t-lg">
        <img
          className="h-full w-full object-cover"
          src={imgSrc || "/images/placeholders/placeholderProductImage.webp"}
          alt="Product-Img"
        />
      </div>

      <div className="flex flex-col gap-3 px-4 py-5">
        {/* Status */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">
            {t("supportStatus.status")}
          </span>
          <span
            className={`rounded-full px-[9px] py-[4px] text-xs font-semibold ${statusStyles[status].bgColor} ${statusStyles[status].textColor}`}
          >
            {t(`supportStatus.statuses.${status}`)}
          </span>
        </div>

        {/* Type */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">
            {t("supportStatus.requestType")}
          </span>
          <span
            className={`rounded-full px-[9px] py-[4px] text-xs font-semibold ${typeStyles[type].bgColor} ${typeStyles[type].textColor}`}
          >
            {t(`supportStatus.types.${type}`)}
          </span>
        </div>

        {/* Subject */}
        <div className="flex flex-col gap-1">
          <label className="block text-sm font-semibold text-gray-700">
            {t("supportStatus.subject")}
          </label>
          <div
            className="w-full truncate rounded-md border border-gray-300 bg-gray-50 p-2 text-sm"
            title={subject}
            style={{ direction: subjectDirection }}
          >
            {subject}
          </div>
        </div>

        {/* Message */}
        <div className="flex flex-col gap-1">
          <label className="block text-sm font-semibold text-gray-700">
            {t("supportStatus.body")}
          </label>
          <div
            className="w-full truncate rounded-md border border-gray-300 bg-gray-50 p-2 text-sm"
            title={message}
            style={{ direction: bodyDirection }}
          >
            {message}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SupportStatusCards;
